/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import appSettings from "appsettings.json";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useMaterialUIController, setLayout } from "context";
import AppLoader from "components/AppLoader";
import CommonFooter from "components/CommonFooter";
import CommonStoreHeader from "components/CommonStoreHeader";
import fetchWithAuth from "layouts/tables/data/api";

function Profile() {
  const [, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState({
    name: "",
    billingAddress: "",
    phoneNumber: "",
    email: "",
  });

  useEffect(() => {
    setLayout(dispatch, "page");

    const loadCurrentUser = async () => {
      try {
        setLoading(true);
        setError("");
        const url = `${appSettings.baseUrl}${appSettings.endpoints.currentUser}`;
        const response = await fetchWithAuth(url, { method: "GET" });
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || "Failed to load profile.");
        }

        const user = await response.json();
        setProfile({
          name: user?.name || "",
          billingAddress: user?.billingAddress || "",
          phoneNumber: user?.phoneNumber || "",
          email: user?.email || "",
        });
      } catch (ex) {
        setError(ex.message || "Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };

    loadCurrentUser();
    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  const initials = useMemo(() => {
    const cleaned = (profile.name || "").trim();
    if (!cleaned) return "NA";
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }, [profile.name]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");

      const email = (profile.email || "").trim();
      const name = (profile.name || "").trim();
      const billingAddress = (profile.billingAddress || "").trim();
      const phoneNumber = (profile.phoneNumber || "").trim();

      if (!name) {
        throw new Error("Name is required.");
      }
      if (!billingAddress) {
        throw new Error("Billing address is required.");
      }
      if (!phoneNumber) {
        throw new Error("Phone number is required.");
      }

      const updateUrl = `${appSettings.baseUrl}${appSettings.endpoints.updateCurrentUserContact}`;
      const updateResponse = await fetchWithAuth(updateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          billingAddress,
          ...(email ? { email } : {}),
        }),
      });

      if (!updateResponse.ok) {
        const message = await updateResponse.text();
        throw new Error(message || "Failed to update profile in auth server.");
      }

      const syncUrl = `${appSettings.baseUrl}${appSettings.endpoints.syncCurrentMemberFromAuth}`;
      const syncResponse = await fetchWithAuth(syncUrl, { method: "POST" });
      if (!syncResponse.ok) {
        const message = await syncResponse.text();
        throw new Error(message || "Profile updated in auth, but failed to sync eshop profile.");
      }

      setProfile((prev) => ({ ...prev, name, email, billingAddress }));
    } catch (ex) {
      setError(ex.message || "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        background:
          "linear-gradient(180deg, rgba(183, 28, 42, 0.06) 0%, rgba(255,255,255,0) 240px), #f8fafc",
      }}
    >
      <CommonStoreHeader profileInitials={initials} />

      {loading ? (
        <Box
          sx={{
            minHeight: "45vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AppLoader size={34} color="#b71c2a" />
        </Box>
      ) : (
        <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5 }, py: 2.6 }}>
          <Typography
            sx={{
              fontSize: { xs: "2rem", md: "2.35rem" },
              color: "#7f1d1d",
              fontWeight: 900,
              letterSpacing: "0.01em",
            }}
          >
            My Account
          </Typography>
          <Typography sx={{ color: "#475569", mb: 2.4, fontSize: { xs: "1rem", md: "1.08rem" } }}>
            Keep your contact and billing details up to date for smooth checkout.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 2.2,
                  borderRadius: 2.3,
                  border: "1px solid #fecdd3",
                  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.08)",
                  background: "linear-gradient(145deg, #fff1f2 0%, #ffffff 68%)",
                }}
              >
                <Stack spacing={1.2}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: "1.23rem" }}>
                    {profile.name || "N/A"}
                  </Typography>
                  <Typography sx={{ color: "#64748b" }}>Primary Contact</Typography>
                  <Typography sx={{ color: "#0f172a", fontWeight: 700 }}>
                    {profile.phoneNumber || "N/A"}
                  </Typography>
                  <Typography sx={{ color: "#0f172a", fontWeight: 700 }}>
                    {profile.email || "N/A"}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      px: 1.2,
                      py: 1,
                      borderRadius: 1.2,
                      bgcolor: "#fff1f2",
                      color: "#7f1d1d",
                      fontSize: "0.9rem",
                      border: "1px solid #fecdd3",
                    }}
                  >
                    Billing address is used for invoices and delivery communication.
                  </Box>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 2.3,
                  border: "1px solid #fecdd3",
                  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.08)",
                  background: "linear-gradient(180deg, #fff1f2 0%, #ffffff 36%)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.35rem", md: "1.68rem" },
                    color: "#7f1d1d",
                    fontWeight: 900,
                    mb: 2,
                  }}
                >
                  Edit Profile Information
                </Typography>

                <Typography sx={{ mb: 0.6, color: "#334155", fontWeight: 600 }}>Name</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={profile.name}
                  onChange={(event) =>
                    setProfile((prev) => ({ ...prev, name: event.target.value }))
                  }
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.2,
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#fda4af" },
                      "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                    },
                  }}
                />

                <Typography sx={{ mb: 0.6, color: "#334155", fontWeight: 600 }}>
                  Billing Address
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={profile.billingAddress}
                  onChange={(event) =>
                    setProfile((prev) => ({ ...prev, billingAddress: event.target.value }))
                  }
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.2,
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#fda4af" },
                      "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                    },
                  }}
                />

                <Typography sx={{ mb: 0.6, color: "#334155", fontWeight: 600 }}>
                  Phone Number
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={profile.phoneNumber}
                  disabled
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.2,
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#fda4af" },
                      "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                    },
                  }}
                />

                <Typography sx={{ mb: 0.6, color: "#334155", fontWeight: 600 }}>Email</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={profile.email}
                  onChange={(event) =>
                    setProfile((prev) => ({ ...prev, email: event.target.value }))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.2,
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#fda4af" },
                      "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                    },
                  }}
                />

                <Stack direction="row" spacing={1.2} sx={{ mt: 2.5 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || loading}
                    sx={{
                      px: 3.4,
                      borderRadius: 1.6,
                      bgcolor: "#b71c2a",
                      color: "#ffffff",
                      fontWeight: 800,
                      boxShadow: "0 10px 20px rgba(183, 28, 42, 0.26)",
                      "&:hover": { bgcolor: "#991b1b", color: "#ffffff" },
                    }}
                  >
                    {saving ? "SAVING..." : "SAVE CHANGES"}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={saving}
                    onClick={() => navigate("/")}
                    sx={{
                      px: 3,
                      borderRadius: 1.6,
                      borderColor: "#fda4af",
                      color: "#7f1d1d",
                      fontWeight: 700,
                      "&:hover": {
                        borderColor: "#fb7185",
                        bgcolor: "#fff1f2",
                      },
                    }}
                  >
                    CANCEL
                  </Button>
                </Stack>
                {error && (
                  <Typography sx={{ color: "#dc2626", mt: 1.5, fontSize: "0.9rem" }}>
                    {error}
                  </Typography>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      <CommonFooter />
    </Box>
  );
}

export default Profile;
